import { Tooltip } from '@mui/material';
import { Button } from '@wot/components';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { ARROWDOWN_ICON, PRO } from '../../assets/icons';
import { PROFILE_PIC_PLACEHOLDER } from '../../assets/images';
import BottomSheet from '../../components/Bottomsheet';
import Calender from '../../components/Calender/Calender';
import CalenderProvider from '../../components/Calender/CalenderContext';
import SetCalender from '../../components/SetCalender';
import { useAppSelector } from '../../hooks/useRedux';
import {
  IProfessionHobby,
  IProfessionSkill,
  IProfessionSubject,
  ITutorEducation,
  ITutorExperience,
  ITutorPersonal,
  ITutorProfessionCategory
} from '@wot/typings';
import { format, parseJSON } from 'date-fns';

export default function Profile() {
  const { profile } = useAppSelector((state) => state.profileSlice);
  const [calenderVisibility, setCalenderVisibility] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    profile ? null : navigate('/profilesetup');
  }, []);

  if (!profile) {
    return null;
  }

  return (
    <div className="w-full mx-auto flex flex-col items-center gap-4 py-4 p-2 grow bg-neutralGray4">
      <div className="w-full flex md:w-[90%] lg:flex-row flex-col gap-5">
        <Link to="/account" className="w-full lg:ml-auto flex lg:hidden justify-end">
          <Button text="Edit Details" type="s-outlined" onClick={() => { }} />
        </Link>

        <div className="lg:w-1/4 w-full h-full flex flex-col gap-7 bg-neutralBlue2 rounded-md p-4">
          <div className="w-full flex flex-col gap-7">
            <Personal personal={profile.personal.data} agreement={profile.agreement} />
          </div>

          <div className="w-full flex flex-col gap-7 py-4 border-t">
            <Education education={profile.education.data} />
          </div>

          <div className="w-full flex flex-col gap-7 py-4 border-t">
            <Experience experience={profile.experience.data} />
          </div>
        </div>

        <div className="lg:w-3/4 w-full h-full rounded-md gap-5 flex flex-col">
          <div className="lg:w-max lg:ml-auto lg:flex lg:justify-end hidden">
            <Link to="/account">
              <Button text="Edit Details" type="s-outlined" onClick={() => { }} />
            </Link>
          </div>
          <div className="w-full flex flex-col gap-2 bg-white p-4 rounded">
            <h1 className="text-xl font-semibold"></h1>
            {profile.profession.data.categoryType === 'subject' &&
              profile.profession.data.categories.map((cate, index) => (
                <SubjectItem key={index} subject={cate as ITutorProfessionCategory<IProfessionSubject>} />
              ))}
            {profile.profession.data.categoryType === 'hobby' &&
              profile.profession.data.categories.map((cate, index) => (
                <HobbyItem key={index} hobby={cate as ITutorProfessionCategory<IProfessionHobby>} />
              ))}

            {profile.profession.data.categoryType === 'skill' &&
              profile.profession.data.categories.map((cate, index) => (
                <SkillItem key={index} skill={cate as ITutorProfessionCategory<IProfessionSkill>} />
              ))}
          </div>

          {profile?.video?.status === 'complete' && (
            <div className="bg-white p-4 rounded-2xl flex flex-col items-center">
              <span className="text-2xl font-medium text-black py-2">Intro Video</span>
              <div className="px-4 py-2">
                <iframe className="md:w-96 w-72 md:h-64 h-52" src={profile.video.link}></iframe>
              </div>
            </div>
          )}

          <div className="w-full h-full bg-white rounded-2xl p-4 flex flex-col  gap-4">
            <h1 className="text-center text-xl font-semibold">Availability</h1>

            <div className="w-full max-h-[700px] gap-4 overflow-hidden overflow-y-scroll no-scrollbar">
              <CalenderProvider>
                <Calender availableSlots={profile.calender} />
              </CalenderProvider>
            </div>
            <div className="min-w-[164px] mx-auto">
              <Button text="Edit Availability" onClick={() => setCalenderVisibility(true)} type="primary" />
            </div>
          </div>
        </div>
      </div>
      {calenderVisibility && (
        <BottomSheet>
          <div className="w-full h-full flex flex-col overflow-y-scroll">
            <div className="sticky top-0 z-40 w-full flex items-center justify-center px-7 md:px-10 py-7 pb-4 bg-white">
              <h1 className="text-2xl font-semibold mx-auto">Set Availability</h1>
              <MdClose className="right-0 text-2xl cursor-pointer" onClick={() => setCalenderVisibility(false)} />
            </div>
            <div className="w-full px-7 md:px-10 pb-4 mx-auto">
              <SetCalender setCalenderVisibility={setCalenderVisibility} />
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

interface PersonalDetails {
  personal: ITutorPersonal;
  agreement: any;
}

function Personal({ personal, agreement }: PersonalDetails) {
  return (
    <>
      <div className="w-full flex flex-col gap-2 bg-neutralBlue2">
        {agreement.level === 'premium' ? (
          <div className="flex justify-end">
            <Tooltip title="Premium Tutor" arrow>
              <img src={PRO} alt="Pro badge" className="h-7 w-7" />
            </Tooltip>
          </div>
        ) : null}
        <div className="flex flex-col items-center justify-center gap-2">
          <img
            src={personal.profilePic === '' ? PROFILE_PIC_PLACEHOLDER : personal.profilePic}
            alt="profile-pic"
            className="w-20 h-20 object-cover bg-white rounded-full"
          />
          <div className="w-full text-center">
            <p className="text-base font-medium overflow-clip">{personal.name}</p>
          </div>
          <div className="w-full text-center">
            <p className="text-sm font-medium overflow-hidden text-ellipsis">{personal.email}</p>
            <p className="text-sm font-medium">{personal.phoneNumber}</p>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <ul className="w-full list-disc ml-4 flex flex-col gap-4">
          <li className="text-sm whitespace-pre-wrap">
            {personal.address.street},{personal.address.houseNo},{personal.address.city},{personal.address.state},
            {personal.address.pincode},{personal.address.country}
          </li>
          <li className="text-sm">Date of birth: {format(parseJSON(personal.dateOfBirth), 'yyyy-LL-dd')}</li>
          <li className="text-sm">Gender: {personal.gender}</li>
          <li className="text-sm">
            <p className="text-sm font-medium">
              Name as per ID
              <br /> <span className="font-normal">{personal.proofIdName}</span>
            </p>
            <div className="w-full flex flex-col gap-4 my-2 -ml-4">
              <img
                src={personal.aadhaarIdFrontImg}
                alt=""
                className="w-full h-full object-cover rounded-sm border bg-slate-100 min-h-32 max-h-64"
              />

              <img
                src={personal.aadhaarIdFrontImg}
                alt=""
                className="w-full h-full object-cover rounded-sm border bg-slate-100 min-h-32 max-h-64"
              />

              <img
                src={personal.panCardFrontImg}
                alt=""
                className="w-full h-full object-cover rounded-sm border bg-slate-100 min-h-32 max-h-64"
              />
            </div>
          </li>
          <li className="text-sm">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Bio</p>
              <p className="text-sm">{personal.bio}</p>
            </div>
          </li>

          <li className="text-sm">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Languages Spoken</p>
              <div className="flex flex-wrap">
                {personal.languagesSpoken.map((l) => (
                  <p key={l} className="text-sm">
                    {l},
                  </p>
                ))}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </>
  );
}

export function SubjectItem({ subject }: { subject: ITutorProfessionCategory<IProfessionSubject> }) {
  const [expand, setExpand] = useState(false);
  return (
    <div
      onClick={() => setExpand(!expand)}
      className="w-full flex flex-col gap-4 p-4 bg-neutralBlue2 rounded-md cursor-pointer"
    >
      <div className="w-full flex items-center justify-between">
        <h1 className="text-base font-semibold">{subject.name}</h1>
        <img src={ARROWDOWN_ICON} alt="" className="w-4" />
      </div>
      {expand && (
        <div className="w-full grid lg:grid-cols-2 grid-cols-1 gap-4">
          {subject.board.map((item, index: any) => (
            <div className=" bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg" key={index}>
              <div className="flex flex-col">
                <p className="text-base font-medium">Board:</p>
                <p className="text-neutralGray2">{item.name}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-base font-medium">Grades:</p>
                <div className="flex flex-wrap">
                  {item.grades.length > 0 &&
                    item.grades.map((i, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
              <div className="flex flex-col">
                <p className="text-base font-medium">Experience:</p>
                <p className="text-neutralGray2">
                  {item.experience}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-base font-medium">Speciality:</p>
                <div className="flex flex-wrap">
                  {item.strengths.length > 0 &&
                    item.strengths.map((i, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function HobbyItem({ hobby }: { hobby: ITutorProfessionCategory<IProfessionHobby> }) {
  const [expand, setExpand] = useState(false);
  return (
    <div
      onClick={() => setExpand(!expand)}
      className="w-full flex flex-col gap-4 p-4 bg-neutralBlue2 rounded-md cursor-pointer"
    >
      <div className="w-full flex items-center justify-between">
        <h1 className="text-base font-semibold">{hobby.name}</h1>
        <img src={ARROWDOWN_ICON} className="w-4" alt="" />
      </div>
      {expand && (
        <div className="w-full grid lg:grid-cols-2 grid-cols-1 gap-4">
          {hobby?.types && hobby?.types?.length > 0 ? (
            <>
              {hobby.types?.map((item, index: any) => (
                <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg" key={index}>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Type:</h3>
                    <p className="text-neutralGray2">{item.name}</p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Levels:</h3>
                    <div className="flex flex-wrap">
                      {item.levels.length > 0 &&
                        item.levels.map((i, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Years of Experience:</h3>
                    <p className="text-neutralGray2">
                      {item.experience}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Speciality:</h3>
                    <div className="flex">
                      {item.strengths.length > 0 &&
                        item.strengths.map((i, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg">
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Levels:</h3>
                <div className="flex flex-wrap">
                  {hobby.levels &&
                    hobby.levels.length > 0 &&
                    hobby.levels.map((i, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Years of Experience:</h3>
                <p className="text-neutralGray2">
                  {hobby.experience}
                </p>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Speciality:</h3>
                <div className="flex flex-wrap">
                  {hobby.strengths &&
                    hobby.strengths.length > 0 &&
                    hobby.strengths.map((i, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SkillItem({ skill }: { skill: ITutorProfessionCategory<IProfessionSkill> }) {
  const [expand, setExpand] = useState(false);
  return (
    <div
      onClick={() => setExpand(!expand)}
      className="w-full flex flex-col gap-4 p-4 bg-neutralBlue2 rounded-md cursor-pointer"
    >
      <div className="w-full flex items-center justify-between">
        <h1 className="text-base font-semibold">{skill.name}</h1>
        <img src={ARROWDOWN_ICON} className="w-4" alt="" />
      </div>
      {expand && (
        <div className="w-full grid lg:grid-cols-2 grid-cols-1 gap-4">
          {skill?.types && skill?.types?.length > 0 ? (
            <>
              {skill.types?.map((item, index: any) => (
                <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg" key={index}>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Type:</h3>
                    <p className="text-neutralGray2">{item.name}</p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Levels:</h3>
                    <div className="flex flex-wrap">
                      {item.levels.length > 0 &&
                        item.levels.map((i, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Years of Experience:</h3>
                    <p className="text-neutralGray2">
                      {item.experience}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Speciality:</h3>
                    <div className="flex flex-wrap">
                      {item.strengths.length > 0 &&
                        item.strengths.map((i, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg">
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Levels:</h3>
                <div className="flex">
                  {skill.levels &&
                    skill.levels.length > 0 &&
                    skill.levels.map((i, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Years of Experience:</h3>
                <p className="text-neutralGray2">
                  {skill.experience}
                </p>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Speciality:</h3>
                <div className="flex">
                  {skill.strengths &&
                    skill.strengths.length > 0 &&
                    skill.strengths.map((i, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface EducationProps {
  education: ITutorEducation[];
}

function Education({ education }: EducationProps) {
  return (
    <>
      <div className="w-full flex flex-col gap-4">
        <h1 className="text-lg font-medium">Education</h1>
        <ul className="w-full list-disc ml-4 flex flex-col gap-4">
          {education.map((education, index) => (
            <li className="text-sm" key={index}>
              {education.degree}, {education.course} {education.specialisation}, {education.university}, {education.year}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
interface ExperienceDetails {
  experience: ITutorExperience[];
}
function Experience({ experience }: ExperienceDetails) {
  return (
    <>
      <div className="w-full flex flex-col gap-4">
        <h1 className="text-lg font-medium">Expreience</h1>
        <ul className="w-full list-disc ml-4 flex flex-col gap-4">
          {experience.map((experience, index) => (
            <li className="text-sm" key={index}>
              <div className="flex flex-col gap-2 text-sm">
                <p>{experience.organization}</p>
                <p>{experience.type}</p>
                <p>
                  {experience.workingSince.start.month}/{experience.workingSince.start.year} to{' '}
                  {experience.workingSince.end ?
                    experience.workingSince.end.month + "/" + experience.workingSince.end.year
                    :
                    "currently working"
                  }
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
